import { HttpErrorResponse } from '@angular/common/http';

/**
 * Largest file the upload endpoints accept. Mirrors the multer `fileSize` limit configured on the
 * micro-service, so the browser rejects an oversized file before it ever reaches the network.
 */
export const MAX_UPLOAD_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = 1024 * 1024;
const GENERIC_UPLOAD_ERROR = 'Error uploading file';
const UNREACHABLE_MESSAGE = 'Could not reach the server. Check your connection and try again.';

/**
 * Renders a byte count the way a user would read it, e.g. `10MB`, `24.3MB` or `812KB`.
 */
export const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0B';
  }
  if (bytes >= BYTES_PER_MB) {
    const megabytes = bytes / BYTES_PER_MB;
    // Rounded up, never to nearest: a file one byte over the limit rendered as `10.0MB` made the
    // message contradict itself — "File exceeds the 10MB limit (your file is 10.0MB)".
    return `${Number.isInteger(megabytes) ? megabytes : Math.ceil(megabytes * 10) / 10}MB`;
  }
  if (bytes >= BYTES_PER_KB) {
    const kilobytes = Math.round(bytes / BYTES_PER_KB);
    return kilobytes >= 1024 ? '1MB' : `${kilobytes}KB`;
  }
  return `${Math.round(bytes)}B`;
};

/**
 * Returns a user-facing reason why the file cannot be uploaded, or `null` when it is acceptable.
 */
export const getFileSizeError = (file: File, maxBytes: number = MAX_UPLOAD_FILE_SIZE_BYTES): string | null => {
  if (!file) {
    return null;
  }
  if (file.size > maxBytes) {
    return `File exceeds the ${formatFileSize(maxBytes)} limit (your file is ${formatFileSize(file.size)}).`;
  }
  return null;
};

/**
 * Turns a failed upload into a message that tells the user what actually went wrong.
 *
 * A size rejection arrives either as a 413 from the app or as a 500 wrapping multer's untranslated
 * `File too large`. The backend's own 413 wording is preferred when present: it is written for a
 * person and it stays right if the server limit ever moves ahead of the one compiled in here.
 *
 * Only 4xx bodies are shown verbatim, since those carry validation messages written for the user.
 * A 5xx body reports infrastructure detail (bucket names, driver errors, stack traces) that a
 * clinical user can do nothing with and that we would rather not put on screen, so those are
 * reduced to a generic message and left in full in the console for debugging.
 *
 * Note that status 0 is deliberately NOT treated as a size rejection. It means the browser could not
 * read the response at all — offline, DNS, TLS, an aborted request — and since oversized files are
 * now stopped before they reach the network, telling that user their file is too big would send them
 * off shrinking a PDF when what failed is their connection.
 */
export const getUploadErrorMessage = (error: unknown, fallback: string = GENERIC_UPLOAD_ERROR): string => {
  const sizeLimitMessage = `File exceeds the ${formatFileSize(MAX_UPLOAD_FILE_SIZE_BYTES)} limit.`;
  const backendMessage =
    typeof (error as HttpErrorResponse)?.error?.message === 'string'
      ? (error as HttpErrorResponse).error.message.trim()
      : '';
  const errorMessage = typeof (error as Error)?.message === 'string' ? (error as Error).message.trim() : '';

  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) {
      return UNREACHABLE_MESSAGE;
    }
    if (error.status === 413) {
      return backendMessage && !isFileTooLargeMessage(backendMessage) ? backendMessage : sizeLimitMessage;
    }
    if (isFileTooLargeMessage(backendMessage)) {
      return sizeLimitMessage;
    }
    if (error.status >= 400 && error.status < 500 && backendMessage) {
      return backendMessage;
    }
    return fallback;
  }

  return isFileTooLargeMessage(errorMessage) ? sizeLimitMessage : fallback;
};

const isFileTooLargeMessage = (message: string): boolean =>
  /file too large|payload too large|request entity too large/i.test(message);
